import { useState, useCallback, useMemo, useEffect } from "react";
import { SignInEvents } from "../../../app-admin/auth/events";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { IShopSignInFormModel, IShopSignInValueModel, IShopSignInModelChange } from "./shop-signin-iface";
import { IBeginShoppingRequest } from "../../../entities-generated/models/project/begin-shopping-request";
import { IShopSignInEditingModel, ShopSignInEditingModel } from "../entities/sign-in-editing-model";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:catalogCode/validated/string/string%%
export const catalogCode = new (class catalogCodeService extends ValidatedFormValueService<IShopSignInEditingModel, string, string> {
    public initialize(data: IShopSignInEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.catalogCode));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IShopSignInEditingModel): void {
        data.catalogCode = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Your code please');
        if(result.validity === BwjValidityState.Valid && /^[a-z0-9]{4}$/i.test(value) === false) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Oops - this isn\'t right...';
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        verificationFailed.setInputValue(false);
        value = value.toUpperCase();
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'catalogCode' // id
    ,'Your personal catalog code' // description
);
// %%END:catalogCode/validated/string/string%%

// %%BEGIN:houseNumber/validated/string/string%%
export const houseNumber = new (class houseNumberService extends ValidatedFormValueService<IShopSignInEditingModel, string, string> {
    public initialize(data: IShopSignInEditingModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IShopSignInEditingModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        if(value.length > 15) {
            value = value.substring(0, 15);
        }
        const result = this.requiredFieldValidator(value, 'Your house # please');
        if(result.validity === BwjValidityState.Valid && /^[a-z0-9 -]+$/i.test(value) === false) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Sorry, this isn\'t right...'
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        verificationFailed.setInputValue(false);
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'houseNumber' // id
    ,'Your house number' // description
);
// %%END:houseNumber/validated/string/string%%

// %%BEGIN:verificationFailed/mutable/boolean/boolean%%
export const verificationFailed = new (class verificationFailedService extends MutableFormValueService<IShopSignInEditingModel, boolean, boolean> {
    public initialize(data: IShopSignInEditingModel): void {
        this.value = false;
    }

    public applyToDataModel(data: IShopSignInEditingModel): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'verificationFailed' // id
    ,'' // description
);
// %%END:verificationFailed/mutable/boolean/boolean%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IShopSignInFormModel => {
    return {
       catalogCode: catalogCode.getInputValue(),
       houseNumber: houseNumber.getInputValue(),
       verificationFailed: verificationFailed.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IShopSignInValueModel => {
    return {
       catalogCode: catalogCode.getOutputValue(),
       houseNumber: houseNumber.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        catalogCode.getInputValue(),
        houseNumber.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IShopSignInModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(SignInEvents.SignInFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IShopSignInFormModel>> => {
    const promises = [
        catalogCode.formValidateAndUpdate(),
        houseNumber.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IShopSignInFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IShopSignInValueModel, dataModel: IShopSignInEditingModel } = {
    dataModel: new ShopSignInEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IShopSignInEditingModel) => {
    const dataModel = data || new ShopSignInEditingModel();

    catalogCode.initialize(dataModel);
    houseNumber.initialize(dataModel);
    verificationFailed.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IShopSignInFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
catalogCode.setChangePublisher(broadcastChange);
houseNumber.setChangePublisher(broadcastChange);
verificationFailed.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new ShopSignInEditingModel();

    catalogCode.applyToDataModel(dataModel);
    houseNumber.applyToDataModel(dataModel);
    verificationFailed.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IShopSignInModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], SignInEvents.SignInFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IShopSignInFormModel => {
    const [getter, setter] = useState<IShopSignInFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IShopSignInModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IShopSignInFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('ShopSignInFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('ShopSignInFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toStartRequest = () => {
    const v = toValueModel();
    const request: IBeginShoppingRequest = {
        houseNumber: v.houseNumber,
        catalogCode: v.catalogCode,
    };
    return request;
}

// ##END:DEVELOPERCODE##
